import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { type PatchDocument } from "../domain/patch";
import {
  getStorageOverview,
  listStoredPatches,
  loadAttachmentBinaries,
  persistPatch,
  type StorageOverview,
} from "../persistence/database";
import { enqueuePersistence } from "../persistence/write-queue";
import {
  applicationSettingsSchema,
  defaultApplicationSettings,
  loadApplicationSettings,
  saveApplicationSettings,
  type ApplicationSettings,
} from "../settings/application-settings";
import { useEditorStore } from "../app/store";
import {
  createLibraryBundle,
  createPatchBundle,
  LIBRARY_BUNDLE_EXTENSION,
  PATCH_BUNDLE_EXTENSION,
  readBundle,
  safeBundleFilename,
  type ParsedBundle,
} from "./bundles";

export type DataDialogMode = "import" | "export" | "settings";

type DialogProps = {
  mode: DataDialogMode;
  onClose: () => void;
  onModeChange: (mode: DataDialogMode) => void;
};

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

function download(bytes: Uint8Array, filename: string) {
  const blob = new Blob([Uint8Array.from(bytes)], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function libraryBundle(activeDocument: PatchDocument) {
  await enqueuePersistence(() => persistPatch(activeDocument));
  const [documents, settings] = await Promise.all([
    enqueuePersistence(() => listStoredPatches()),
    loadApplicationSettings(),
  ]);
  const attachments = await loadAttachmentBinaries(
    documents.flatMap((document) => document.attachments.map((attachment) => attachment.id)),
  );
  return createLibraryBundle(documents, {
    activePatchId: activeDocument.id,
    applicationSettings: settings,
    attachments,
  });
}

function DialogFrame({
  children,
  onClose,
  title,
}: {
  children: React.ReactNode;
  onClose: () => void;
  title: string;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [
        ...dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => !element.hidden);
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable.at(-1)!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose]);

  return (
    <div className="data-dialog-backdrop">
      <section
        aria-labelledby="data-dialog-title"
        aria-modal="true"
        className="data-dialog"
        ref={dialogRef}
        role="dialog"
      >
        <div className="data-dialog-header">
          <div>
            <span className="eyebrow">Local data</span>
            <h1 id="data-dialog-title">{title}</h1>
          </div>
          <button
            aria-label="Close"
            className="icon-button data-dialog-close"
            onClick={onClose}
            ref={closeRef}
            type="button"
          >
            <Icon name="close" />
          </button>
        </div>
        {children}
      </section>
    </div>
  );
}

function ExportDialog({ onClose }: Pick<DialogProps, "onClose">) {
  const activeDocument = useEditorStore((state) => state.activeDocument);
  const patchCount = useEditorStore((state) => state.patchLibrary.length);
  const [busy, setBusy] = useState<"patch" | "library" | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const run = async (kind: "patch" | "library") => {
    setBusy(kind);
    setMessage(null);
    try {
      if (kind === "patch") {
        const attachments = await loadAttachmentBinaries(
          activeDocument.attachments.map((attachment) => attachment.id),
        );
        const bytes = await createPatchBundle(activeDocument, "0.1.0", attachments);
        download(bytes, safeBundleFilename(activeDocument.title, PATCH_BUNDLE_EXTENSION));
        setMessage("Patch bundle created and downloaded.");
      } else {
        const bytes = await libraryBundle(activeDocument);
        download(bytes, safeBundleFilename("patchwork-library", LIBRARY_BUNDLE_EXTENSION));
        setMessage(`Complete backup created for ${patchCount} patches.`);
      }
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  return (
    <DialogFrame onClose={onClose} title="Export and backup">
      <div className="data-dialog-body export-options">
        <article className="data-option-card">
          <div className="data-option-icon">
            <Icon name="export" />
          </div>
          <div>
            <h2>Current patch</h2>
            <p>
              One portable, versioned <code>.patchdoc</code> bundle containing this patch and its
              integrity manifest.
            </p>
          </div>
          <button disabled={busy !== null} onClick={() => void run("patch")} type="button">
            {busy === "patch" ? "Creating…" : "Download patch"}
          </button>
        </article>
        <article className="data-option-card">
          <div className="data-option-icon">
            <Icon name="save" />
          </div>
          <div>
            <h2>Complete library backup</h2>
            <p>
              All {patchCount} local patches and application settings in one verified{" "}
              <code>.patchbackup</code> file.
            </p>
          </div>
          <button disabled={busy !== null} onClick={() => void run("library")} type="button">
            {busy === "library" ? "Creating…" : "Download backup"}
          </button>
        </article>
        <p className="data-safety-note">
          <Icon name="help" size={15} /> Browser storage is not a permanent backup. Keep exported
          files somewhere you already back up.
        </p>
        {message && (
          <p aria-live="polite" className="data-dialog-message">
            {message}
          </p>
        )}
      </div>
    </DialogFrame>
  );
}

function ImportDialog({ onClose }: Pick<DialogProps, "onClose">) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { importDocuments, libraryOperation, patchLibrary } = useEditorStore();
  const [filename, setFilename] = useState("");
  const [bundle, setBundle] = useState<ParsedBundle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [replaceConfirmed, setReplaceConfirmed] = useState(false);
  const existingIds = useMemo(() => new Set(patchLibrary.map((patch) => patch.id)), [patchLibrary]);
  const conflicts =
    bundle?.documents.filter((document) => existingIds.has(document.id)).length ?? 0;

  const chooseFile = async (file?: File) => {
    if (!file) return;
    setError(null);
    setResult(null);
    setBundle(null);
    setFilename(file.name);
    try {
      setBundle(await readBundle(file));
    } catch (readError) {
      setError(errorMessage(readError));
    }
  };

  const apply = async (conflict: "keep-both" | "replace", replaceLibrary = false) => {
    if (!bundle) return;
    setError(null);
    try {
      const outcome = await importDocuments(bundle.documents, {
        conflict,
        replaceLibrary,
        preferredActivePatchId:
          bundle.manifest.kind === "library"
            ? bundle.manifest.activePatchId
            : bundle.manifest.patchId,
        attachments: bundle.attachments,
      });
      if (bundle.applicationSettings !== undefined) {
        const parsedSettings = applicationSettingsSchema.safeParse(bundle.applicationSettings);
        if (parsedSettings.success) await saveApplicationSettings(parsedSettings.data);
      }
      setResult(
        `${outcome.imported} ${outcome.imported === 1 ? "patch" : "patches"} imported${outcome.copied ? `; ${outcome.copied} kept as a new copy` : ""}.`,
      );
    } catch (importError) {
      setError(errorMessage(importError));
    }
  };

  return (
    <DialogFrame onClose={onClose} title="Import patches">
      <div className="data-dialog-body">
        <input
          accept=".patchdoc,.patchbackup,.zip,application/zip"
          className="visually-hidden"
          onChange={(event) => void chooseFile(event.target.files?.[0])}
          ref={inputRef}
          tabIndex={-1}
          type="file"
        />
        {!bundle && !result && (
          <button
            className="file-drop-target"
            onClick={() => inputRef.current?.click()}
            type="button"
          >
            <Icon name="import" size={28} />
            <strong>Select a Patchwork bundle</strong>
            <span>
              <code>.patchdoc</code> or <code>.patchbackup</code>, up to 100 MB
            </span>
          </button>
        )}
        {filename && !bundle && !result && <p className="selected-file">Checking {filename}…</p>}
        {bundle && !result && (
          <>
            <section className="bundle-summary">
              <span className="bundle-kind">
                {bundle.manifest.kind === "library" ? "LIBRARY BACKUP" : "PATCH BUNDLE"}
              </span>
              <h2>{bundle.manifest.kind === "patch" ? bundle.manifest.title : filename}</h2>
              <dl>
                <div>
                  <dt>Patches</dt>
                  <dd>{bundle.documents.length}</dd>
                </div>
                <div>
                  <dt>Existing IDs</dt>
                  <dd>{conflicts}</dd>
                </div>
                <div>
                  <dt>Exported</dt>
                  <dd>{new Date(bundle.manifest.exportedAt).toLocaleString()}</dd>
                </div>
                <div>
                  <dt>Integrity</dt>
                  <dd>Verified</dd>
                </div>
              </dl>
            </section>
            <div className="import-actions">
              <button
                disabled={Boolean(libraryOperation)}
                onClick={() => void apply("keep-both")}
                type="button"
              >
                {conflicts ? "Import and keep both" : "Import"}
              </button>
              {conflicts > 0 && (
                <button
                  className="secondary"
                  disabled={Boolean(libraryOperation)}
                  onClick={() => void apply("replace")}
                  type="button"
                >
                  Replace {conflicts} matching {conflicts === 1 ? "patch" : "patches"}
                </button>
              )}
            </div>
            {bundle.manifest.kind === "library" && (
              <section className="danger-zone">
                <h2>Replace the entire local library</h2>
                <p>
                  This removes every local patch before restoring this backup. Export your current
                  library first if it is not already backed up.
                </p>
                <label>
                  <input
                    checked={replaceConfirmed}
                    onChange={(event) => setReplaceConfirmed(event.target.checked)}
                    type="checkbox"
                  />
                  I understand that patches not in this backup will be removed
                </label>
                <button
                  disabled={!replaceConfirmed || Boolean(libraryOperation)}
                  onClick={() => void apply("replace", true)}
                  type="button"
                >
                  Replace entire library
                </button>
              </section>
            )}
          </>
        )}
        {result && (
          <div className="import-result" role="status">
            <Icon name="check" size={28} />
            <h2>Import complete</h2>
            <p>{result}</p>
            <button onClick={onClose} type="button">
              Done
            </button>
          </div>
        )}
        {error && (
          <p className="data-dialog-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </DialogFrame>
  );
}

function formatBytes(value?: number) {
  if (value === undefined) return "Unavailable";
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 ** 3) return `${(value / 1024 ** 2).toFixed(1)} MB`;
  return `${(value / 1024 ** 3).toFixed(1)} GB`;
}

function SettingsDialog({ onClose, onModeChange }: Omit<DialogProps, "mode">) {
  const { activeDocument, updatePrintSettings } = useEditorStore();
  const [settings, setSettings] = useState<ApplicationSettings>(defaultApplicationSettings);
  const [printSettings, setPrintSettings] = useState(activeDocument.printSettings);
  const [storage, setStorage] = useState<StorageOverview | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([loadApplicationSettings(), getStorageOverview()]).then(
      ([loaded, overview]) => {
        setSettings(loaded);
        setStorage(overview);
      },
    );
  }, []);

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await saveApplicationSettings(settings);
      updatePrintSettings(printSettings);
      setMessage("Settings saved.");
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const backup = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const bytes = await libraryBundle(activeDocument);
      download(bytes, safeBundleFilename("patchwork-library", LIBRARY_BUNDLE_EXTENSION));
      setMessage("Complete library backup downloaded.");
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogFrame onClose={onClose} title="Settings">
      <div className="data-dialog-body settings-sections">
        <section>
          <h2>Interface</h2>
          <label className="setting-row">
            <span>
              <strong>Interface scale</strong>
              <small>Increase application chrome and supporting text.</small>
            </span>
            <select
              value={settings.uiScale}
              onChange={(event) =>
                setSettings({
                  uiScale: Number(event.target.value) as ApplicationSettings["uiScale"],
                })
              }
            >
              <option value="100">100%</option>
              <option value="112.5">112.5%</option>
              <option value="125">125%</option>
            </select>
          </label>
        </section>
        <section>
          <h2>Current patch print output</h2>
          <div className="settings-grid">
            <label>
              <span>Paper</span>
              <select
                value={printSettings.paper}
                onChange={(event) =>
                  setPrintSettings({
                    ...printSettings,
                    paper: event.target.value as "a4" | "letter",
                  })
                }
              >
                <option value="a4">A4</option>
                <option value="letter">Letter</option>
              </select>
            </label>
            <label>
              <span>Orientation</span>
              <select
                value={printSettings.orientation}
                onChange={(event) =>
                  setPrintSettings({
                    ...printSettings,
                    orientation: event.target.value as "landscape" | "portrait",
                  })
                }
              >
                <option value="landscape">Landscape</option>
                <option value="portrait">Portrait</option>
              </select>
            </label>
            <label>
              <span>Palette</span>
              <select
                value={printSettings.palette}
                onChange={(event) =>
                  setPrintSettings({
                    ...printSettings,
                    palette: event.target.value as "monochrome" | "grayscale",
                  })
                }
              >
                <option value="monochrome">Monochrome</option>
                <option value="grayscale">Grayscale</option>
              </select>
            </label>
          </div>
        </section>
        <section>
          <h2>Local storage</h2>
          <dl className="storage-grid">
            <div>
              <dt>Patches</dt>
              <dd>{storage?.patchCount ?? "…"}</dd>
            </div>
            <div>
              <dt>Recoverable drafts</dt>
              <dd>{storage?.draftCount ?? "…"}</dd>
            </div>
            <div>
              <dt>Patch documents</dt>
              <dd>{formatBytes(storage?.documentBytes)}</dd>
            </div>
            <div>
              <dt>Photos</dt>
              <dd>{formatBytes(storage?.photoBytes)}</dd>
            </div>
            <div>
              <dt>Audio samples</dt>
              <dd>{formatBytes(storage?.audioBytes)}</dd>
            </div>
            <div>
              <dt>Attachments</dt>
              <dd>{storage?.attachmentCount ?? "…"}</dd>
            </div>
            <div>
              <dt>Browser usage</dt>
              <dd>{formatBytes(storage?.usage)}</dd>
            </div>
            <div>
              <dt>Reported quota</dt>
              <dd>{formatBytes(storage?.quota)}</dd>
            </div>
            <div>
              <dt>Persistent storage</dt>
              <dd>{storage ? (storage.persisted ? "Granted" : "Not granted") : "…"}</dd>
            </div>
          </dl>
          <div className="settings-backup-actions">
            <button disabled={busy} onClick={() => void backup()} type="button">
              Download complete backup
            </button>
            <button
              className="secondary"
              disabled={busy}
              onClick={() => onModeChange("import")}
              type="button"
            >
              Restore or merge backup
            </button>
          </div>
        </section>
        <footer className="settings-footer">
          {message && <span aria-live="polite">{message}</span>}
          <button className="secondary" onClick={onClose} type="button">
            Close
          </button>
          <button disabled={busy} onClick={() => void save()} type="button">
            Save settings
          </button>
        </footer>
      </div>
    </DialogFrame>
  );
}

export function DataPortabilityDialog({ mode, onClose, onModeChange }: DialogProps) {
  if (mode === "import") return <ImportDialog onClose={onClose} />;
  if (mode === "export") return <ExportDialog onClose={onClose} />;
  return <SettingsDialog onClose={onClose} onModeChange={onModeChange} />;
}
